/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `reportConstants` for the Caintra Connect frontend.
 * @module utils/reportConstants
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
export const REPORT_FILE_DATE_FORMAT = "DD-MM-YYYY-HH-mm-ss";
export const APPOINTMENT_DETAIL_REPORT_PREFIX = "reporte-detalle-citas";
export const REQUIREMENTS_REPORT_PREFIX = "reporte-requerimientos";
export const ACCUMULATED_CONNECTIONS_REPORT_PREFIX =
  "reporte-acumulado-vinculaciones";
export const ATTENDANCES_REPORT_PREFIX = "reporte-asistencias";
export const SAC_REPORT_PREFIX = "reporte-sac";
export const GENERAL_B2B_REPORT_PREFIX = "reporte-general-b2b";
export const VIRTUAL_EVENT_REGISTRATION_REPORT_PREFIX =
  "reporte-registros-evento-virtual";

/** First calendar year available in report year selectors. */
export const REPORT_YEAR_START = 2025;

/** Numeric ids aligned with `getReportsOptions` (value 0–13). */
export const REPORT_OPTION = {
  TABLES: 0,
  AUTHENTICATION_LOGS: 1,
  BUYER_LOGS: 2,
  B2B_ATTENDANCE: 3,
  MEMBER_INVOICE: 4,
  NON_MEMBER_INVOICE: 5,
  GENERAL_APPOINTMENTS: 6,
  APPOINTMENT_DETAILS: 7,
  ACCUMULATED_CONNECTIONS: 8,
  REQUIREMENTS: 9,
  EVENT_ATTENDANCES: 10,
  SAC: 11,
  GENERAL_B2B: 12,
  VIRTUAL_REGISTRATION: 13,
};

/** Report types that bind the shared business-event selector. */
export const BUSINESS_EVENT_REPORT_OPTIONS = [
  REPORT_OPTION.GENERAL_APPOINTMENTS,
  REPORT_OPTION.APPOINTMENT_DETAILS,
  REPORT_OPTION.REQUIREMENTS,
  REPORT_OPTION.VIRTUAL_REGISTRATION,
];

/** Report types that bind year plus business-event selectors. */
export const YEAR_EVENT_REPORT_OPTIONS = [
  REPORT_OPTION.MEMBER_INVOICE,
  REPORT_OPTION.NON_MEMBER_INVOICE,
  REPORT_OPTION.GENERAL_APPOINTMENTS,
  REPORT_OPTION.APPOINTMENT_DETAILS,
  REPORT_OPTION.REQUIREMENTS,
  REPORT_OPTION.VIRTUAL_REGISTRATION,
];

/** Shared i18n keys for the Reports page filter labels. */
export const REPORT_I18N_KEYS = {
  SELECT_FILTERS_LABEL: "pages.reports.selectFiltersLabel",
  EVENT: "pages.reports.event",
  EVENT_PLACEHOLDER: "pages.reports.eventPlaceholder",
  COMPANY: "pages.reports.company",
  COMPANY_PLACEHOLDER: "pages.reports.companyPlaceholder",
  YEAR: "pages.reports.year",
  YEAR_PLACEHOLDER: "pages.reports.yearPlaceholder",
};

/**
 * Builds a timestamped report filename.
 * @param {string} prefix - Filename prefix without extension
 * @param {string} dateTime - Preformatted date-time suffix
 * @param {string} [extension="xlsx"] - File extension
 * @returns {string}
 */
export const buildReportFilename = (
  prefix,
  dateTime,
  extension = "xlsx",
) => `${prefix}-${dateTime}.${extension}`;
